<template>
  <component
    :is="tag"
    v-bind="elementAttrs"
    :class="buttonClasses"
    :aria-label="props.ariaLabel"
    :aria-describedby="props.ariaDescribedBy"
    data-cy="ds-button"
    @click="handleClick"
  >
    <DSIcon
      v-if="showPrefixIcon"
      :icon="props.prefixIcon!"
      :size="iconSize"
    />

    <slot />

    <DSIcon
      v-if="showSuffixIcon"
      :icon="props.suffixIcon!"
      :size="iconSize"
    />
  </component>
</template>

<script setup lang="ts">
import { computed } from "vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { DS_ICON_SIZES } from "@/src/design_system/primitives/icon/types";
import {
  DS_BUTTON_SIZE,
  DS_BUTTON_TYPE,
  DS_BUTTON_VARIANT,
  DSButtonProps,
} from "@/src/design_system/button_and_controls/button/types/props";
import {
  DS_BUTTON_ALIGN_TO_CLASS_MAP,
  DS_BUTTON_CLASSES,
  DS_BUTTON_SIZE_TO_ICON_SIZE_MAP,
  DS_BUTTON_VARIANT_TO_CLASS_MAP,
} from "@/src/design_system/button_and_controls/button/constants/classes";
import { getPaletteClass } from "@/src/design_system/shared_modules/palette/utils/palette";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";

const props = withDefaults(defineProps<DSButtonProps>(), {
  variant: DS_BUTTON_VARIANT.Primary,
  danger: false,
  size: DS_BUTTON_SIZE.Medium,
  disabled: false,
  type: DS_BUTTON_TYPE.Button,
  fullWidth: false,
  form: undefined,
  turbo: undefined,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const isLink = computed(() => !!props.href);
const tag = computed(() => (isLink.value ? "a" : "button"));

// props.turbo, when explicitly set (true or false), always wins over the implicit
// derivation from the other turbo-related props.
const resolveDataTurbo = (implicitTurbo: boolean): boolean | undefined =>
  props.turbo !== undefined ? props.turbo : implicitTurbo || undefined;

const buttonDataAttrs = computed(() => {
  const dataAttrs: Record<string, string | boolean | undefined> = {
    "data-turbo-frame": props.turboFrame,
    "data-turbo-submits-with": props.turboSubmitsWith,
    "data-turbo": resolveDataTurbo(!!(props.turboFrame || props.turboSubmitsWith)),
  };

  return dataAttrs;
});

const linkDataAttrs = computed(() => {
  const dataAttrs: Record<string, string | boolean | undefined> = {
    "data-turbo-frame": props.turboFrame,
    "data-turbo-method": props.turboMethod,
    "data-turbo-confirm": props.turboConfirm,
    "data-action": props.action,
    "data-turbo": resolveDataTurbo(!!(props.turboFrame || props.turboMethod || props.turboConfirm || props.action)),
  };

  return dataAttrs;
});

const elementAttrs = computed(() => {
  if (isLink.value) {
    return {
      href: props.disabled ? undefined : props.href,
      target: props.target,
      role: "button",
      "aria-disabled": props.disabled || undefined,
      tabindex: props.disabled ? -1 : props.tabindex,
      ...linkDataAttrs.value,
    };
  }
  return {
    type: props.type,
    disabled: props.disabled,
    tabindex: props.tabindex,
    form: props.form,
    formaction: props.formAction,
    formmethod: props.formMethod,
    name: props.name,
    value: props.value,
    onclick: props.onclick,
    ...buttonDataAttrs.value,
  };
});

const { stateClasses } = useStateClasses(props);

const isDanger = computed(() => props.danger && props.variant !== DS_BUTTON_VARIANT.Tertiary);

const buttonClasses = computed(() => [
  {
    [DS_BUTTON_CLASSES.Root]: true,
    [DS_BUTTON_CLASSES.Danger]: isDanger.value,
    [DS_BUTTON_CLASSES.FullWidth]: props.fullWidth,
    ...stateClasses.value,
  },
  DS_BUTTON_VARIANT_TO_CLASS_MAP[props.variant],
  `${DS_BUTTON_CLASSES.Root}--${props.size}`,
  props.align ? DS_BUTTON_ALIGN_TO_CLASS_MAP[props.align] : undefined,
  isDanger.value ? undefined : getPaletteClass(props.colorPalette),
]);

const iconSize = computed(() => {
  return props.size ? DS_BUTTON_SIZE_TO_ICON_SIZE_MAP[props.size] : DS_ICON_SIZES.Medium;
});

const showPrefixIcon = computed(() => {
  return !!props.prefixIcon;
});

const showSuffixIcon = computed(() => {
  return !!props.suffixIcon;
});

const handleClick = (event: MouseEvent) => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }
  emit("click", event);
};
</script>
